import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoFocus", "clearableValues", "value", "onChange", "defaultRecipient", "index", "relatedRecipients", "emailBlocklistStatus"];
import { memo, useCallback, useRef, useState, forwardRef, useMemo, useImperativeHandle } from 'react';
import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import I18n from 'I18n';
// @ts-expect-error module not typed
import EmailAddressRecord from 'customer-data-email/schema/email/EmailAddressRecord';
import { useContactPermissions } from 'conversations-thread-data/contact-permissions/public/hooks';
import { validateEmail } from 'composer-data/email/public/operators';
import { postRecipientSearch } from 'composer-data/contacts/public/clients';
import debounce from 'react-utils/debounce';
// @ts-expect-error module not typed
import { getEmail } from 'conversations-internal-schema/visitor/operators/visitorGetters';
import { getEmailAddressRecordFromString } from 'conversations-thread-data/common-reply-metadata/public/operators/getEmailAddressRecordFromString';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { useEmailDeliverabilityStatuses } from 'composer-data/email/public/hooks';
import { getEmailAddressOptions } from '../operators/getEmailAddressOptions';
import CustomRecipientTag from './CustomRecipientTag';
import { Select } from 'foundations-components/transitional/components/Select';
import { jsx as _jsx } from "react/jsx-runtime";
const SEARCH_INPUT_DEBOUNCE_MS = 100;
const EMAIL_SEPARATOR = ',';
function makeCreateNewOption(query) {
  const searchEmails = query.split(EMAIL_SEPARATOR);
  const text = searchEmails.length === 1 ? I18n.text('composer-ui.metadata-editor.email-recipient-search-input.create-new-option', {
    recipientEmail: searchEmails[0]
  }) : searchEmails.length === 2 ? I18n.text('composer-ui.metadata-editor.email-recipient-search-input.create-two-new-options', {
    email1: searchEmails[0],
    email2: searchEmails[1]
  }) : I18n.text('composer-ui.metadata-editor.email-recipient-search-input.create-more-new-options', {
    count: searchEmails.length - 2,
    email1: searchEmails[0],
    email2: searchEmails[1]
  });
  return [{
    address: query,
    clearableValue: true,
    name: '',
    text,
    value: query
  }];
}
const _EmailRecipientSearchInput = (props, ref) => {
  const {
      autoFocus,
      clearableValues,
      value,
      onChange,
      defaultRecipient,
      index,
      relatedRecipients,
      emailBlocklistStatus
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const defaultRecipientAddress = getEmail(defaultRecipient);
  const defaultRecipientEmailRecord = defaultRecipientAddress && getEmailAddressRecordFromString(defaultRecipientAddress, defaultRecipient);
  const internalValue = value.toArray().map(emailAddressRecord => emailAddressRecord.address);
  const {
    deliverabilityStatuses
  } = useEmailDeliverabilityStatuses({
    emailAddresses: internalValue
  });
  const {
    contactPermissions
  } = useContactPermissions({
    emailAddresses: ImmutableSet(internalValue)
  });
  const getValidationData = emailAddress => {
    return validateEmail(emailAddress, deliverabilityStatuses, contactPermissions);
  };
  const searchResults = useRef({});
  const searchText = useRef('');
  const [options, setOptions] = useState();

  // generate options and value for values that may have been cached and
  // won't be in the search results
  const valueOptions = getEmailAddressOptions(value, {
    clearable: clearableValues,
    getValidationData,
    emailBlocklistStatus
  });
  const handleChange = useCallback(({
    target: {
      value: changeValue
    }
  }) => {
    // map search results to id-keyed email address records
    const indexedSearchResults = {};
    Object.values(searchResults.current).forEach(emailAddressRecords => {
      emailAddressRecords.forEach(emailAddressRecord => {
        indexedSearchResults[emailAddressRecord.address] = emailAddressRecord;
      });
    });
    const commaSeparatedValues = changeValue.reduce((acc, newValue) => {
      acc.push(...newValue.split(EMAIL_SEPARATOR).map(email => email.trim()));
      return acc;
    }, []);

    // filter out addresses that may have been removed
    const nextValue = value.filter(emailAddressRecord => commaSeparatedValues.includes(emailAddressRecord.address));

    // add incoming addresses
    let addedRecipients = ImmutableList();
    commaSeparatedValues.forEach(address => {
      const hasValue = nextValue.find(emailAddressRecord => emailAddressRecord.address === address);
      if (!hasValue) {
        if (defaultRecipientAddress === address) {
          addedRecipients = addedRecipients.push(defaultRecipientEmailRecord);
        } else if (indexedSearchResults[address]) {
          addedRecipients = addedRecipients.push(indexedSearchResults[address]);
        } else if (relatedRecipients) {
          const relatedRecipient = relatedRecipients.find(recipient => recipient.address === address);
          if (relatedRecipient) {
            addedRecipients = addedRecipients.push(relatedRecipient);
          }
        }
      }
    });
    onChange(ImmutableList(nextValue.concat(addedRecipients)));
  }, [onChange, value, defaultRecipientAddress, defaultRecipientEmailRecord, relatedRecipients]);
  const createNewEmailEntries = useCallback(emails => {
    return emails.map(address => new EmailAddressRecord({
      address: address.trim()
    }));
  }, []);
  const handleLoadOptions = useMemo(() => debounce(userQuery => {
    searchText.current = userQuery;
    if (!userQuery || !userQuery.length) {
      setOptions(undefined);
      return;
    }
    postRecipientSearch({
      searchTerm: userQuery,
      deliveryIdentifier: 'email',
      query: {
        requestAction: 'COMMUNICATE'
      }
    }).then(emailAddresses => {
      searchResults.current[userQuery] = emailAddresses;
      const newOptions = getEmailAddressOptions(ImmutableList(emailAddresses), {
        clearable: clearableValues,
        emailBlocklistStatus
      });
      // bake incoming results in with the options for existing values
      setOptions(newOptions);
    }).catch(error => {
      reportError({
        error: new Error(`Error in email recipient search: ${error.message}`),
        extra: {
          error,
          requestArgs: {
            searchTerm: userQuery
          }
        }
      });
    });
  }, SEARCH_INPUT_DEBOUNCE_MS), [clearableValues, emailBlocklistStatus]);

  // Get options logic which creates and passes new option if current user query does not return any contacts
  // Otherwise return options (from API) with already selected options OR only selected options with a dummy
  const getOptions = () => {
    const shouldSuggestDefaultAddress = defaultRecipientAddress && !internalValue.includes(defaultRecipientAddress);

    // identify the recipients that were originally included, but deleted by the user
    const unselectedRelatedRecipients = relatedRecipients && relatedRecipients.filter(recipient => {
      if (recipient.address === defaultRecipientAddress) return false;
      return !internalValue.includes(recipient.address);
    }).toList();
    const hasUnselectedRecipients = unselectedRelatedRecipients && unselectedRelatedRecipients.size;
    const optionsContainsString = element => {
      return element.text.toLowerCase().includes(searchText.current && searchText.current.toLowerCase());
    };
    if (options && options.filter(optionsContainsString).length > 0) {
      return valueOptions.concat(...options);
    } else if (searchText.current.trim()) {
      searchResults.current[searchText.current] = createNewEmailEntries(searchText.current.split(EMAIL_SEPARATOR));
      return valueOptions.concat(makeCreateNewOption(searchText.current));
    } else if (shouldSuggestDefaultAddress || hasUnselectedRecipients) {
      let builtOptions = valueOptions;
      if (shouldSuggestDefaultAddress) {
        const defaultRecipientOption = getEmailAddressOptions(ImmutableList([defaultRecipientEmailRecord]), {
          clearable: true,
          emailBlocklistStatus
        });
        builtOptions = builtOptions.concat(defaultRecipientOption);
      }
      if (hasUnselectedRecipients) {
        const unselectedOptions = getEmailAddressOptions(unselectedRelatedRecipients, {
          clearable: true,
          emailBlocklistStatus
        });
        builtOptions = builtOptions.concat(unselectedOptions);
      }
      return builtOptions;
    } else {
      // dummyOption for bypassing default 'all options are selected` text
      const dummyOption = {
        name: '',
        address: '',
        text: I18n.text('composer-ui.metadata-editor.email-recipient-search-input.type-to-search'),
        value: '',
        clearableValue: false,
        disabled: true
      };
      return valueOptions.concat([dummyOption]);
    }
  };
  const [selectRef, setSelectRef] = useState(null);
  useImperativeHandle(ref, () => {
    // @ts-expect-error we are assuming that the input ref is a RefObject
    const currentDefault = ref && ref.current && ref.current.defaultField;
    return {
      index,
      currentField: selectRef,
      defaultField: index === 0 ? selectRef : currentDefault || null,
      hasValue: internalValue && internalValue.length > 0
    };
  }, [ref, index, internalValue, selectRef]);
  return /*#__PURE__*/_jsx(Select, Object.assign({}, rest, {
    flex: 1,
    autoFocus: autoFocus,
    valueComponent: CustomRecipientTag,
    autoload: false,
    cache: {},
    clearable: false,
    minimumSearchCount: 2,
    multi: true,
    onChange: handleChange,
    onInputChange: handleLoadOptions,
    options: getOptions(),
    placeholder: I18n.text('composer-ui.metadata-editor.email-recipient-search-input.placeholder'),
    searchPromptText: '',
    value: internalValue,
    ref: setSelectRef
  }));
};
_EmailRecipientSearchInput.displayName = 'EmailRecipientSearchInput';
const EmailRecipientSearchInput = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(_EmailRecipientSearchInput));
export default EmailRecipientSearchInput;